/**
 * AdminLangContext
 * ─────────────────────────────────────────────────────────────
 * Provides an independent language state for the admin panel.
 * Priority: localStorage → browser/device language → "zh"
 * Persisted in localStorage under "tabifoto_admin_lang".
 * Completely isolated from the public-facing i18n language.
 */
import { createContext, useCallback, useContext, useState, type ReactNode } from "react";

export type AdminLang = "zh" | "en" | "ja";

interface AdminLangContextValue {
  adminLang: AdminLang;
  setAdminLang: (lang: AdminLang) => void;
  /** Returns the value of the correct lang field from an object like { zh: "...", en: "...", ja: "..." } */
  resolveI18n: (obj: Partial<Record<AdminLang, string>> | undefined) => string;
}

const AdminLangContext = createContext<AdminLangContextValue | null>(null);

const STORAGE_KEY = "tabifoto_admin_lang";

/** Detect browser/device preferred language and map to supported AdminLang */
function detectBrowserLang(): AdminLang {
  try {
    const langs = Array.from(navigator.languages ?? [navigator.language ?? "en"]);
    for (const lang of langs) {
      const base = lang.split("-")[0].toLowerCase();
      if (base === "zh") return "zh";
      if (base === "ja") return "ja";
      if (base === "en") return "en";
    }
  } catch {
    // ignore
  }
  return "zh";
}

function loadLang(): AdminLang {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === "zh" || v === "en" || v === "ja") return v;
  } catch {
    // ignore
  }
  // No saved preference — fall back to browser language
  return detectBrowserLang();
}

export function AdminLangProvider({ children }: { children: ReactNode }) {
  const [adminLang, setLangState] = useState<AdminLang>(loadLang);

  const setAdminLang = useCallback((lang: AdminLang) => {
    setLangState(lang);
    try { localStorage.setItem(STORAGE_KEY, lang); } catch { /* ignore */ }
  }, []);

  const resolveI18n = useCallback(
    (obj: Partial<Record<AdminLang, string>> | undefined): string => {
      if (!obj) return "";
      return obj[adminLang] ?? obj.zh ?? obj.en ?? obj.ja ?? "";
    },
    [adminLang]
  );

  return (
    <AdminLangContext.Provider value={{ adminLang, setAdminLang, resolveI18n }}>
      {children}
    </AdminLangContext.Provider>
  );
}

export function useAdminLang(): AdminLangContextValue {
  const ctx = useContext(AdminLangContext);
  if (!ctx) throw new Error("useAdminLang must be used within AdminLangProvider");
  return ctx;
}
